import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Shield, AlertTriangle, Users, Plus, Eye, Settings } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useToast } from "../../hooks/use-toast";
import { getAuthHeaders } from "../../lib/api";

interface ActiveVisit {
  visit: {
    id: number;
    plateNumber?: string;
    driverName?: string;
    status: string;
    startedAt: string;
  };
  session?: {
    id: number;
    status: string;
  };
  lane?: {
    id: number;
    name: string;
  };
}

interface ActiveVisitsProps {
  visits: ActiveVisit[] | undefined;
  isLoading: boolean;
}

export default function ActiveVisits({ visits, isLoading }: ActiveVisitsProps) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState<ActiveVisit | null>(null);
  const [isAssignLaneOpen, setIsAssignLaneOpen] = useState(false);
  const [selectedLane, setSelectedLane] = useState("");
  const [formData, setFormData] = useState({
    plateNumber: "",
    driverName: "",
    purpose: "weighing",
    orderExternalId: ""
  });
  const [page, setPage] = useState(1);
  const [allVisits, setAllVisits] = useState<ActiveVisit[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [totalVisits, setTotalVisits] = useState(0);
  const limit = 50;
  
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  React.useEffect(() => {
    if (visits) {
      if (page === 1) {
        setAllVisits(visits);
        setHasMore(visits.length >= limit);
      }
    }
  }, [visits, page]);
  
  const loadMore = async () => {
    try {
      const offset = page * limit;
      const response = await fetch(`/api/dashboard/active-visits?limit=${limit}&offset=${offset}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to load more visits');
      const newVisits = await response.json();
      setAllVisits([...allVisits, ...newVisits]);
      setHasMore(newVisits.length >= limit);
      setPage(page + 1);
    } catch (error) {
      console.error('Failed to load more visits:', error);
      toast({
        title: "Error",
        description: "Failed to load more visits",
        variant: "destructive",
      });
    }
  };

  // Create visit mutation
  const createVisitMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await fetch("/api/visits/entry", {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          siteId: 1,
          plateNumber: data.plateNumber,
          driverName: data.driverName,
          purpose: data.purpose,
          orderExternalId: data.orderExternalId || null
        })
      });
      
      if (!response.ok) {
        throw new Error("Failed to create visit");
      }
      
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/active-visits"] });
      toast({
        title: "Visit Created",
        description: `New visit for ${formData.plateNumber} has been created successfully`,
      });
      setIsCreateOpen(false);
      resetForm();
    },
    onError: () => {
      toast({
        title: "Creation Failed",
        description: "Failed to create new visit",
        variant: "destructive",
      });
    }
  });

  // Assign lane mutation
  const assignLaneMutation = useMutation({
    mutationFn: async ({ visitId, laneId }: { visitId: number; laneId: string }) => {
      const response = await fetch(`/api/visits/${visitId}/assign-lane`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer demo-token-${Date.now()}`
        },
        body: JSON.stringify({ laneId })
      });
      if (!response.ok) throw new Error("Failed to assign lane");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/active-visits"] });
      toast({
        title: "Lane Assigned",
        description: "Vehicle has been assigned to lane successfully"
      });
      setIsAssignLaneOpen(false);
      setSelectedVisit(null);
      setSelectedLane("");
    },
    onError: () => {
      toast({
        title: "Assignment Failed",
        description: "Failed to assign lane",
        variant: "destructive"
      });
    }
  });

  const handleViewVisit = (visit: ActiveVisit) => {
    setSelectedVisit(visit);
  };

  const handleAssignLane = (visit: ActiveVisit) => {
    setSelectedVisit(visit);
    setIsAssignLaneOpen(true);
  };

  const handleControlSession = async (visit: ActiveVisit) => {
    if (!visit.session) return;
    
    try {
      const response = await fetch(`/api/sessions/${visit.session.id}/control`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer demo-token-${Date.now()}`
        }
      });
      
      if (response.ok) {
        toast({
          title: "Session Controlled",
          description: "Session control command sent successfully"
        });
        queryClient.invalidateQueries({ queryKey: ["/api/dashboard/active-visits"] });
      } else {
        throw new Error("Failed to control session");
      }
    } catch (error) {
      toast({
        title: "Control Failed",
        description: "Failed to control session",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      plateNumber: "",
      driverName: "",
      purpose: "weighing",
      orderExternalId: ""
    });
  };

  const handleSubmit = () => {
    if (!formData.plateNumber || !formData.driverName) {
      toast({
        title: "Missing Information",
        description: "Please fill in plate number and driver name",
        variant: "destructive",
      });
      return;
    }
    createVisitMutation.mutate(formData);
  };
  if (isLoading) {
    return (
      <div className="lg:col-span-2 bg-card rounded-xl border border-border shadow-sm">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Active Visits</h3>
            <div className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "weighing":
        return <Shield className="w-4 h-4 text-primary" />;
      case "hold":
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      default:
        return <Users className="w-4 h-4 text-blue-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "weighing":
        return "bg-green-100 text-green-800";
      case "hold":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  const formatTimeElapsed = (startedAt: string) => {
    const start = new Date(startedAt);
    const now = new Date();
    const diff = Math.floor((now.getTime() - start.getTime()) / 60000);
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    }
    return `${minutes}min`;
  };

  return (
    <div className="lg:col-span-2 bg-card rounded-xl border border-border shadow-sm">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Active Visits</h3>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="button-new-visit">
                <Plus className="w-4 h-4 mr-2" />
                New Visit
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create New Visit</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Plate Number */}
                <div className="space-y-2">
                  <Label htmlFor="plateNumber">Vehicle Plate Number *</Label>
                  <Input
                    id="plateNumber"
                    value={formData.plateNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, plateNumber: e.target.value.toUpperCase() }))}
                    placeholder="e.g., ABC123GP"
                    data-testid="input-plate-number"
                  />
                </div>

                {/* Driver Name */}
                <div className="space-y-2">
                  <Label htmlFor="driverName">Driver Name *</Label>
                  <Input
                    id="driverName"
                    value={formData.driverName}
                    onChange={(e) => setFormData(prev => ({ ...prev, driverName: e.target.value }))}
                    placeholder="Driver full name"
                    data-testid="input-driver-name"
                  />
                </div>

                {/* Purpose */}
                <div className="space-y-2">
                  <Label htmlFor="purpose">Visit Purpose</Label>
                  <Select value={formData.purpose} onValueChange={(value) => setFormData(prev => ({ ...prev, purpose: value }))}>
                    <SelectTrigger data-testid="select-purpose">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weighing">Weighing</SelectItem>
                      <SelectItem value="delivery">Delivery</SelectItem>
                      <SelectItem value="pickup">Pickup</SelectItem>
                      <SelectItem value="inspection">Inspection</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Order ID */}
                <div className="space-y-2">
                  <Label htmlFor="orderExternalId">Order ID (Optional)</Label>
                  <Input
                    id="orderExternalId"
                    value={formData.orderExternalId}
                    onChange={(e) => setFormData(prev => ({ ...prev, orderExternalId: e.target.value }))}
                    placeholder="External order reference"
                    data-testid="input-order-id"
                  />
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-2 pt-4">
                  <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSubmit}
                    disabled={createVisitMutation.isPending}
                    data-testid="button-create-visit"
                  >
                    {createVisitMutation.isPending ? "Creating..." : "Create Visit"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Plate Number</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Lane</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {allVisits && allVisits.length > 0 ? (
              allVisits.map((item) => (
                <tr key={`${item.visit.id}-${item.session?.id ?? 'none'}`} className="hover:bg-muted/30 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mr-3">
                        {getStatusIcon(item.session?.status || "check-in")}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground" data-testid={`visit-plate-${item.visit.id}`}>
                          {item.visit.plateNumber || "No Plate"}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {item.visit.driverName || "Unknown Driver"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.session?.status || "check-in")}`}>
                      <span className={`status-indicator ${item.session?.status === "active" ? "status-online" : "status-warning"}`}></span>
                      {item.session?.status === "active" ? "Weighing" : item.visit.status === "open" ? "Check-in" : "On Hold"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {item.lane?.name || "Entry"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {formatTimeElapsed(item.visit.startedAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-primary hover:text-primary/80 mr-3" 
                          onClick={() => handleViewVisit(item)}
                          data-testid={`button-view-${item.visit.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Visit Details</DialogTitle>
                        </DialogHeader>
                        {selectedVisit && (
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="text-sm font-medium">Visit ID</label>
                                <p className="text-sm text-muted-foreground">{selectedVisit.visit.id}</p>
                              </div>
                              <div>
                                <label className="text-sm font-medium">Plate Number</label>
                                <p className="text-sm text-muted-foreground">{selectedVisit.visit.plateNumber || "Not provided"}</p>
                              </div>
                              <div>
                                <label className="text-sm font-medium">Driver Name</label>
                                <p className="text-sm text-muted-foreground">{selectedVisit.visit.driverName || "Not provided"}</p>
                              </div>
                              <div>
                                <label className="text-sm font-medium">Status</label>
                                <Badge variant={selectedVisit.visit.status === "active" ? "default" : "secondary"}>
                                  {selectedVisit.visit.status}
                                </Badge>
                              </div>
                              <div>
                                <label className="text-sm font-medium">Lane</label>
                                <p className="text-sm text-muted-foreground">{selectedVisit.lane?.name || "Not assigned"}</p>
                              </div>
                              <div>
                                <label className="text-sm font-medium">Session</label>
                                <p className="text-sm text-muted-foreground">
                                  {selectedVisit.session ? `Active (ID: ${selectedVisit.session.id})` : "No active session"}
                                </p>
                              </div>
                            </div>
                            <div>
                              <label className="text-sm font-medium">Entry Time</label>
                              <p className="text-sm text-muted-foreground">
                                {new Date(selectedVisit.visit.startedAt).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>

                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-accent hover:text-accent/80" 
                      onClick={() => item.session ? handleControlSession(item) : handleAssignLane(item)}
                      data-testid={`button-control-${item.visit.id}`}
                    >
                      <Settings className="w-4 h-4 mr-1" />
                      {item.session ? "Control" : "Assign Lane"}
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-muted-foreground">
                  No active visits
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Load More Button */}
      {hasMore && !isLoading && allVisits.length > 0 && (
        <div className="flex justify-center mt-4">
          <Button 
            variant="outline" 
            onClick={loadMore}
            disabled={isLoading}
          >
            Load More ({allVisits.length} loaded)
          </Button>
        </div>
      )}

      {/* Assign Lane Dialog */}
      <Dialog open={isAssignLaneOpen} onOpenChange={setIsAssignLaneOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Lane</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedVisit && (
              <div className="space-y-2">
                <p className="text-sm">
                  <span className="font-medium">Vehicle:</span> {selectedVisit.visit.plateNumber || "Unknown"}
                </p>
                <p className="text-sm">
                  <span className="font-medium">Driver:</span> {selectedVisit.visit.driverName || "Unknown"}
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="lane-select">Select Lane</Label>
              <Select value={selectedLane} onValueChange={setSelectedLane}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a lane" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Lane 1 - Inbound</SelectItem>
                  <SelectItem value="2">Lane 2 - Outbound</SelectItem>
                  <SelectItem value="3">Lane 3 - Heavy Vehicles</SelectItem>
                  <SelectItem value="4">Lane 4 - Light Vehicles</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsAssignLaneOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  if (selectedVisit && selectedLane) {
                    assignLaneMutation.mutate({ 
                      visitId: selectedVisit.visit.id, 
                      laneId: selectedLane 
                    });
                  }
                }}
                disabled={!selectedLane || assignLaneMutation.isPending}
              >
                {assignLaneMutation.isPending ? "Assigning..." : "Assign Lane"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
